import { getDatesForPaymentsFetch } from "components/Payment/utils";
import { recallFilter, resetSortLabelArrow, saveFilter } from "components/shared/ManagementGeneralTable/utils";
import { formatDateToIso, getJsonOrFalse, getSubObject, isObjectEmpty } from "helpers/utils";
import { useEffect, useRef, useState } from "react";
import { getAdminPortalUserListPaginated, getLogList } from "store/adminPanelSlice";
import { fetchAllAdvanceSlices } from "store/advancePaymentSlice";
import type { AgreementStatus } from "store/agreementsStore";
import type { ArticleCompletionStatus } from "store/articleStore";
import { downloadDAPFilePress, fetchAllArticlesPaginated } from "store/articleStore";
import { fetchBeneficiaries, fetchBeneficiariesAutocomplete } from "store/beneficiaryStore";
import { fetchAnalyseHistory } from "store/contributionStore";
import type { ImportSourceType } from "store/filesHistorySlice";
import { fetchExportedFiles, fetchImportedFiles } from "store/filesHistorySlice";
import { fetchBeneficiariesInformation, fetchSalesByChannel, getGeneralTabData } from "store/managementStore";
import { fetchPaymentsBetweenDates, fetchPaymentsHistory, getPaymentLots } from "store/paymentSlice";
import { fetchLocations, fetchStocks } from "store/stockStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitles, fetchTitleSales, fetchTitlesAutocomplete } from "store/titleStore";
import { fetchTransferees, fetchTransfereesAutocomplete, fetchTransfereeTerms } from "store/transfereesStore";
import { fetchTransferTermSales } from "store/transferTermSalesStore";
import { fetchTransferTerms } from "store/transferTermsStore";
import type { AgreementSignedStatus, TFetchStatus, TFilterParamsPaymentStatus, TParamsPagination } from "store/types";
import type { TPortalType } from "store/types";
import type { IDatesRange, TExportTargets, TImportTargets, TSortOrder } from "types";

import { useFirstRender } from "./useFirstRender";

export type TPaginationType =
    | "terms"
    | "titles"
    | "beneficiaries"
    | "beneficiariesInfo"
    | "stock"
    | "salesByChannel"
    | "advancePayment"
    | "logs"
    | "adminPortalUsers"
    | "analyseHistory"
    | "transferTerms"
    | "transferees"
    | "transferTermSales"
    | "transfereeTerms"
    | "articles"
    | "titleSales"
    | "articles-download-dap"
    | "paymentLots"
    | "paymentsHistory"
    | "paymentsWaiting"
    | "paymentsPress"
    | "importFiles"
    | "exportFiles";

type TProps = {
    type: TPaginationType | undefined;
    itemId?: number; // If we fetch data for a specific item
    defaultLimitPagination?: number;
    portal?: Exclude<TPortalType, "urssaf">;
    autocomplete?: boolean; // if the api response needs to be put in another variable in the state
    condition?: boolean; // if we want to conditionnally call this hook, so it doesn't fetch uselessly, only used in ArticleEdit for now
    sortBy?: string;
    sortOrder?: TSortOrder;
    defaultData?: Partial<TParamsPagination>;
    isPaused?: boolean;
    /** salesByChannel: restrict to this ISBN; omit for every title of the work */
    filterTitleId?: number;
    /** salesByChannel: `term` = package / term_channel_sales only */
    filterSalesScope?: "term";
};

export interface IHandleRequest {
    isInitialLoad?: boolean;
    behavior?: "refresh" | "";
    defaultType?: TProps["type"];
    savedQuery?: string;
    extraOptions?: Partial<TParamsPagination>;
    /** Keep the current rows visible instead of showing the table skeleton. */
    silent?: boolean;
}

export type TPaidStatus = "all" | "paid" | "topay" | "unpaid";

export default function useAPIPagination({
    type,
    itemId,
    defaultLimitPagination,
    autocomplete = false,
    condition: defaultCondition = true,
    sortBy: defaultSortBy,
    sortOrder: defaultSortOrder,
    defaultData,
    isPaused = false,
    filterTitleId,
    filterSalesScope,
}: TProps) {
    const dispatch = useAppDispatch();
    const { activePeriod, periods, periodsDateRange } = useAppSelector((state) => state.period);
    const { locationStatus } = useAppSelector((state) => state.stock);
    const adminPortalUsersLoading = useAppSelector((state) =>
        type === "adminPortalUsers" ? state.adminPanel.adminPortalUsersPaginated?.isLoading ?? false : false,
    );
    const [page, setPage] = useState<number>(0);
    const localSettings = parseInt(localStorage.getItem("settings_pagination") ?? "10");
    const [limitPagination, setLimitPagination] = useState<number>(defaultLimitPagination ?? localSettings);
    const [offsetPagination, setOffsetPagination] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>(defaultSortBy || "");
    const storedOrder = getJsonOrFalse(localStorage.getItem("managementSortBy") ?? "{}")?.sortOrder;
    const [sortOrder, setSortOrder] = useState<TSortOrder>(defaultSortOrder ?? storedOrder ?? "ASC");
    const [query, setQuery] = useState<string>("");
    const [queryIsTyping, setQueryIsTyping] = useState<boolean | null>(null);
    const [chosenTitleTags, setChosenTitleTags] = useState<string[]>([]);
    const [chosenBeneficiaryTags, setChosenBeneficiaryTags] = useState<string[]>([]);
    const [chosenBrand, setChosenBrand] = useState<string>("");
    const [count, setCount] = useState<number>(0);
    const [objectCount, setObjectCount] = useState<any>();
    const [timeOut, setTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [extraParams, setExtraParams] = useState<any>();
    const [control, setControl] = useState<"all" | "valid" | "invalid">("all");
    const [paidStatus, setPaidStatus] = useState<TPaidStatus>("all");
    const [requestStatus, setRequestStatus] = useState<TFetchStatus>("idle");
    const [grossMinimum, setGrossMinimum] = useState<string | undefined>();
    const isFirstRender = useFirstRender();
    const [transferStatus, setTransferStatus] = useState<"all" | number>("all");
    const [year, setYear] = useState<number | null>(defaultData?.year ?? null);
    const [lineType, setLineType] = useState<string | null>(null);
    const [signedStatus, setSignedStatus] = useState<AgreementSignedStatus>("all");
    const [dates, setDates] = useState(defaultData?.dates);
    const [condition, setCondition] = useState<boolean>(defaultCondition);
    const [completionStatus, setCompletionStatus] = useState<ArticleCompletionStatus | "all">("all");
    const [agreementStatus, setAgreementStatus] = useState<AgreementStatus | "all">("all");
    const [paymentLot, setPaymentLot] = useState<number | "all">(defaultData?.paymentLot ?? "all");
    const [beneficiaryIds, setBeneficiaryIds] = useState<number[]>(defaultData?.beneficiaryIds ?? []);
    const [isForfait, setIsForfait] = useState<"0" | "1" | "all">("all");
    const [customStatusId, setCustomStatusId] = useState("all");
    const [method, setMethod] = useState<"all" | "GET" | "POST" | "PUT" | "DELETE">("all");
    const [termId, setTermId] = useState<number | undefined>(defaultData?.termId);
    const [target, setTarget] = useState<Array<TImportTargets | TExportTargets> | "all">("all");
    const [sourceType, setSourceType] = useState<ImportSourceType | "all">("all");

    const latestSalesByChannelRequestId = useRef<string | undefined>();
    // use ref to prevent useEffect to be triggered when isPaused changes
    const isPausedRef = useRef(isPaused);
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    useEffect(() => {
        if (queryIsTyping === false) {
            handleRequest();
        }
    }, [queryIsTyping]);

    useEffect(() => {
        if (!isPausedRef.current) {
            handleRequest();
        }
    }, [
        page,
        limitPagination,
        offsetPagination,
        sortBy,
        sortOrder,
        chosenTitleTags,
        chosenBeneficiaryTags,
        control,
        condition,
        extraParams,
        chosenBrand,
        paidStatus,
        grossMinimum,
        transferStatus,
        year,
        lineType,
        activePeriod,
        signedStatus,
        dates,
        completionStatus,
        agreementStatus,
        paymentLot,
        beneficiaryIds,
        isForfait,
        customStatusId,
        method,
        termId,
        target,
        sourceType,
        filterTitleId,
        filterSalesScope,
    ]);

    /**
     * Fetch request with params
     * @param isInitialLoad Verify if the request is sent for the first loading of the page
     * @param {"refresh"} behavior Call with this param to remove stored options in local storage
     * @param defaultType
     */
    const handleRequest = ({
        isInitialLoad = false,
        behavior,
        defaultType,
        savedQuery,
        extraOptions = {},
        silent = false,
    }: IHandleRequest = {}) => {
        const shouldRefresh = behavior === "refresh";
        let options: TParamsPagination = {};

        limitPagination && (options.limit = limitPagination);
        offsetPagination && (options.offset = offsetPagination);
        sortBy && (options.sortBy = sortBy);
        sortOrder && (options.sortOrder = sortOrder);
        query && (options.query = savedQuery ?? query);
        chosenTitleTags && (options.titleTags = chosenTitleTags);
        chosenBeneficiaryTags && (options.beneficiaryTags = chosenBeneficiaryTags);
        chosenBrand && (options.brand = chosenBrand);
        extraParams && (options.extraParams = extraParams);
        control && (options.control = control);
        paidStatus && (options.paidStatus = paidStatus);
        grossMinimum != undefined && (options.grossDuty = grossMinimum);
        transferStatus && (options.status = transferStatus);
        year && (options.year = year);
        lineType && (options.type = lineType);
        signedStatus && (options.signedStatus = signedStatus);
        dates?.startDate && (options.dates = dates);
        completionStatus && (options.completionStatus = completionStatus);
        agreementStatus && (options.agreementStatus = agreementStatus);
        isForfait && (options.isForfait = isForfait);
        method && (options.method = method);
        termId && (options.termId = termId);
        target && (options.target = target);
        sourceType && (options.sourceType = sourceType);

        if (!isObjectEmpty(extraOptions)) {
            Object.keys(extraOptions).forEach((key) => {
                options[key] = extraOptions[key];
            });
        }

        if (defaultType === "articles-download-dap") {
            options.export = "xlsx";
        }
        // Endpoints for pagination

        const isManualSorted = !!options.sortBy;
        const storedOptions =
            getJsonOrFalse(localStorage.getItem("managementSortBy") ?? "{}")[type ?? "terms"] ?? options;
        const isNewOption = storedOptions.sortBy !== options.sortBy;
        // save and store local storage sort preferences + adapt mui head cell table style
        if (!isObjectEmpty(options) && (defaultType ?? type) === "terms") {
            if (isManualSorted) {
                saveFilter(options, isNewOption, storedOptions, type);
            }
            if (query) {
                resetSortLabelArrow(storedOptions);
            }
            if (isFirstRender || isInitialLoad) {
                options = {
                    ...options,
                    query: "",
                    sortBy: storedOptions.sortBy,
                    sortOrder: storedOptions.sortOrder,
                };
                recallFilter(options);
            }
            if (shouldRefresh) {
                resetSortLabelArrow(storedOptions);
                localStorage.removeItem("managementSortBy");
            }
        }

        switch (defaultType ?? type) {
            case "terms":
                if (activePeriod) {
                    options["periodId"] = autocomplete
                        ? periods.filter((item) => item.isActive).map((item) => item.id)
                        : options.periodId ?? activePeriod;

                    // Check for CommandPalette search query in localStorage
                    const commandPaletteQuery = localStorage.getItem("commandPaletteSearchQuery");
                    if (commandPaletteQuery && !options.query) {
                        options.query = commandPaletteQuery;
                        // Update local query state to reflect the search
                        setQuery(commandPaletteQuery);
                    }

                    // Pre-fill search from advance payment impact modal (EAN13 / ISBN filter)
                    const managementInitialSearch = sessionStorage.getItem("management_initial_search");
                    if (managementInitialSearch && !options.query) {
                        options.query = managementInitialSearch;
                        setQuery(managementInitialSearch);
                    }

                    setRequestStatus("pending");
                    dispatch(getGeneralTabData(options))
                        .then((resp) => {
                            if (resp.meta.requestStatus === "fulfilled") {
                                setCount(resp.payload.count);
                            }
                            // Clear CommandPalette search from localStorage after fetch
                            if (commandPaletteQuery) {
                                localStorage.removeItem("commandPaletteSearchQuery");
                            }
                            if (managementInitialSearch) {
                                sessionStorage.removeItem("management_initial_search");
                            }
                        })
                        .finally(() => {
                            setRequestStatus("succeeded");
                        });
                }
                break;
            case "titles":
                options.periodId = activePeriod;
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(autocomplete ? fetchTitlesAutocomplete(options) : fetchTitles(options)).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "beneficiaries":
                options.periodId = activePeriod;
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(autocomplete ? fetchBeneficiariesAutocomplete(options) : fetchBeneficiaries(options)).then(
                    (resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                    },
                );
                break;
            case "beneficiariesInfo":
                if (activePeriod) {
                    options.periodId = activePeriod;
                    const _options = getSubObject(options, [
                        "limit",
                        "offset",
                        "periodId",
                        "query",
                        "sortBy",
                        "sortOrder",
                        "paidStatus",
                        "beneficiaryTags",
                    ]);
                    setRequestStatus("pending");
                    dispatch(fetchBeneficiariesInformation(_options)).then((resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "stock":
                if (activePeriod) {
                    options.periodId = activePeriod;
                    setRequestStatus("pending");
                    dispatch(fetchStocks(options)).then((resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                    });
                    if (locationStatus === "idle") {
                        dispatch(fetchLocations(activePeriod));
                    }
                }
                break;
            case "salesByChannel":
                if (activePeriod && itemId) {
                    options.periodId = activePeriod;
                    setRequestStatus("pending");
                    const request = dispatch(
                        fetchSalesByChannel({
                            options: options,
                            termId: itemId,
                            titleId: filterTitleId,
                            salesScope: filterSalesScope,
                        }),
                    );
                    latestSalesByChannelRequestId.current = request.requestId;
                    request.then((resp) => {
                        if (resp.meta.requestId !== latestSalesByChannelRequestId.current) return;
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "advancePayment":
                if (activePeriod) {
                    options.periodId = activePeriod;
                    const _dates = dates
                        ? {
                              startDate: formatDateToIso(dates.startDate),
                              endDate: formatDateToIso(dates.endDate),
                          }
                        : undefined;

                    // Pre-fill search from term recap navigation (EAN13 filter)
                    const advanceInitialSearch = sessionStorage.getItem("advance_initial_search");
                    if (advanceInitialSearch && !options.query) {
                        options.query = advanceInitialSearch;
                        setQuery(advanceInitialSearch);
                    }

                    const specificOptions = getSubObject(options, [
                        "limit",
                        "sortOrder",
                        "sortBy",
                        "offset",
                        "periodId",
                        "paidStatus",
                        "query",
                        "brand",
                        "isForfait",
                    ]);

                    if (!silent) setRequestStatus("pending");
                    dispatch(
                        fetchAllAdvanceSlices({
                            options: {
                                ...specificOptions,
                                ..._dates,
                                ...(customStatusId !== "all" ? { customStatusId } : {}),
                            },
                        }),
                    ).then((resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                        // Clear the pre-filter after first fetch
                        if (advanceInitialSearch) {
                            sessionStorage.removeItem("advance_initial_search");
                        }
                    });
                }
                break;
            case "analyseHistory":
                if (extraParams) {
                    setRequestStatus("pending");
                    dispatch(fetchAnalyseHistory(options)).then((resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setObjectCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "transferTerms":
                options.periodId = activePeriod;
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(fetchTransferTerms({ options })).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "transferees":
                options.periodId = activePeriod;
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(autocomplete ? fetchTransfereesAutocomplete({ options }) : fetchTransferees({ options })).then(
                    (resp) => {
                        setRequestStatus("succeeded");
                        if (resp.meta.requestStatus === "fulfilled") {
                            setCount(resp.payload.count);
                        }
                    },
                );
                break;
            case "transferTermSales":
                options.periodId = activePeriod;
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(fetchTransferTermSales({ options })).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "transfereeTerms":
                options.periodId = activePeriod;
                if (!condition || !itemId) return;
                setRequestStatus("pending");
                dispatch(fetchTransfereeTerms({ options, transfereeId: itemId })).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "articles":
                if (!condition) return;
                setRequestStatus("pending");
                dispatch(fetchAllArticlesPaginated(options)).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "articles-download-dap":
                dispatch(downloadDAPFilePress(options));
                break;
            case "titleSales":
                if (!itemId || !condition) return;
                setRequestStatus("pending");
                dispatch(fetchTitleSales({ titleId: itemId, options })).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setRequestStatus("succeeded");
                        setCount(resp.payload.count);
                    }
                });
                break;
            case "paymentsPress":
            case "paymentsWaiting": {
                console.log("extraOptions", extraOptions, options.dates, extraOptions.dates);
                if (!options.dates && !extraOptions.dates) return;
                setRequestStatus("pending");
                if (extraOptions.dates) options.dates = extraOptions.dates;
                if (extraOptions.paidStatus) options.paidStatus = extraOptions.paidStatus;
                const specificOptions = getSubObject(options, [
                    "limit",
                    "sortOrder",
                    "sortBy",
                    "paidStatus",
                    "offset",
                    "query",
                ]);
                const { startDate, endDate } = getDatesForPaymentsFetch({
                    isBookCompany: (defaultType ?? type) === "paymentsWaiting",
                    periodsDate: periodsDateRange,
                    newDates: options.dates as IDatesRange,
                });
                const params = {
                    ...specificOptions,
                    paidStatus: "topay" as TFilterParamsPaymentStatus,
                    startDate,
                    endDate,
                };
                dispatch(fetchPaymentsBetweenDates(params)).then((resp) => {
                    setRequestStatus("succeeded");
                    if (resp.payload?.count !== undefined) setCount(resp.payload.count);
                });
                break;
            }
            case "paymentLots": {
                setRequestStatus("pending");
                if (paymentLot !== "all") options.paymentLot = paymentLot;
                if (paidStatus !== "all") options.paidStatus = paidStatus;
                if (extraOptions.sortOrder) options.sortOrder = extraOptions.sortOrder;

                const specificOptions = getSubObject(options, [
                    "limit",
                    "sortOrder",
                    "sortBy",
                    "paidStatus",
                    "offset",
                    "paymentLot",
                    "query",
                ]);

                dispatch(
                    getPaymentLots({
                        options: specificOptions,
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setRequestStatus("succeeded");
                        setCount(resp.payload.count);
                    }
                });
                break;
            }
            case "paymentsHistory":
                {
                    setRequestStatus("pending");
                    if (paymentLot !== "all") options.paymentLot = paymentLot;
                    if (paidStatus !== "all") options.paidStatus = paidStatus;
                    if (beneficiaryIds && beneficiaryIds.length > 0) options.beneficiaryIds = beneficiaryIds;
                    if (!sortBy) options.sortBy = "paymentLotId";
                    const specificOptions = getSubObject(options, [
                        "limit",
                        "sortOrder",
                        "sortBy",
                        "offset",
                        "paymentLot",
                        "beneficiaryIds",
                        "paidStatus",
                        "query",
                    ]);

                    const historyDates = options.dates?.startDate ? options.dates : dates;
                    const filterByDates = historyDates && specificOptions.paymentLot == null;

                    const _startDate = filterByDates ? formatDateToIso(historyDates.startDate) : undefined;
                    const _endDate = filterByDates ? formatDateToIso(historyDates.endDate) : undefined;

                    dispatch(
                        fetchPaymentsHistory({ startDate: _startDate, endDate: _endDate, options: specificOptions }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            setRequestStatus("succeeded");
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "logs":
                {
                    const _options = getSubObject(options, [
                        "method",
                        "query",
                        "limit",
                        "offset",
                        "sortBy",
                        "sortOrder",
                        "dates",
                    ]);
                    setRequestStatus("pending");
                    dispatch(getLogList({ options: _options })).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            setRequestStatus("succeeded");
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "adminPortalUsers":
                {
                    if (adminPortalUsersLoading && !shouldRefresh) return;
                    const _options = getSubObject(options, ["limit", "offset", "query"]) as any;
                    if (extraParams?.companyId != null) _options.companyId = extraParams.companyId;
                    if (extraParams?.accountType != null) _options.accountType = extraParams.accountType;
                    setRequestStatus("pending");
                    dispatch(getAdminPortalUserListPaginated({ options: _options })).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            setRequestStatus("succeeded");
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "importFiles":
                {
                    const _options = getSubObject(options, [
                        "limit",
                        "offset",
                        "query",
                        "sortBy",
                        "sortOrder",
                        "target",
                        "sourceType",
                    ]);

                    setRequestStatus("pending");
                    dispatch(fetchImportedFiles({ options: _options })).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            setRequestStatus("succeeded");
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            case "exportFiles":
                {
                    const _options = getSubObject(options, [
                        "limit",
                        "offset",
                        "query",
                        "sortBy",
                        "sortOrder",
                        "target",
                    ]);
                    setRequestStatus("pending");
                    dispatch(fetchExportedFiles({ options: _options })).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            setRequestStatus("succeeded");
                            setCount(resp.payload.count);
                        }
                    });
                }
                break;
            default:
                break;
        }
    };

    const handleQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        // Debounce call endpoint when search
        if (!queryIsTyping) {
            setTimeOut(
                setTimeout(() => {
                    setPage(0);
                    setOffsetPagination(0);
                    setQueryIsTyping(false); // handleRequest()
                }, 500),
            );
            setQueryIsTyping(true);
        } else {
            clearTimeout(timeOut);
            setTimeOut(
                setTimeout(() => {
                    setPage(0);
                    setOffsetPagination(0);
                    setQueryIsTyping(false); // handleRequest()
                }, 500),
            );
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setCondition(true);
        setOffsetPagination(newPage * limitPagination);
        setPage(newPage);
    };

    const handleChangeLimit = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newLimit = +event.target.value;
        setCondition(true);
        setPage(Math.floor((offsetPagination + newLimit - 1) / newLimit));
        setLimitPagination(newLimit);
        localStorage.setItem("settings_pagination", event.target.value);
    };

    const handleSort = (name: string) => {
        setSortBy(name);
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    };

    return {
        query,
        setQuery,
        page,
        setPage,
        limitPagination,
        setLimitPagination,
        offsetPagination,
        setOffsetPagination,
        chosenTitleTags,
        setChosenTitleTags,
        chosenBrand,
        setChosenBrand,
        chosenBeneficiaryTags,
        setChosenBeneficiaryTags,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        count,
        objectCount,
        setCount,
        setObjectCount,
        handleQuery,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
        control,
        setControl,
        setExtraParams,
        paidStatus,
        setPaidStatus,
        requestStatus,
        setRequestStatus,
        setGrossMinimum,
        transferStatus,
        setTransferStatus,
        year,
        setYear,
        lineType,
        setLineType,
        signedStatus,
        setSignedStatus,
        completionStatus,
        setCompletionStatus,
        agreementStatus,
        setAgreementStatus,
        paymentLot,
        setPaymentLot,
        beneficiaryIds,
        setBeneficiaryIds,
        isForfait,
        setIsForfait,
        customStatusId,
        setCustomStatusId,
        setDates,
        dates,
        method,
        setMethod,
        target,
        setTarget,
        sourceType,
        setSourceType,
    };
}
